import i18n from 'i18next'
import moment from 'moment'
import 'moment/locale/fr'
import 'moment/locale/nl'
import setLocale from 'yup/lib/setLocale'
import * as networkService from './networkService'
import { getCurrentStepType } from './stepService'
import * as translationTexts from './translationTexts'

const supportedLanguages = [
  {
    value: 'nl',
    locale: 'nl_BE',
    domain: 'mijnhuurprofiel.be',
  },
  {
    value: 'en',
    locale: 'en_US',
    domain: 'mytenantprofile.be',
  },
  {
    value: 'fr',
    locale: 'fr_FR',
    domain: 'monprofillocataire.be',
  },
]

i18n.on('languageChanged', function() {
  console.log(`Language changed to: '${i18n.language}'`)

  setLocalChangeYup()
  moment.locale(i18n.language)
})

export const currentLocale = () => {
  if (i18n.language) {
    return i18n.language
  }

  const hostname = (window.location.hostname || '').toLowerCase()
  const localeFromHostname = supportedLanguages.find(item => hostname.includes(item.domain))
  if (localeFromHostname) {
    return localeFromHostname.locale
  }

  // Fallback
  return 'en_US'
}

export const getSupportedLanguages = () => supportedLanguages

export const mapLocale = locale => {
  if (locale.includes('nl')) {
    return 'nl_BE'
  }

  if (locale.includes('fr')) {
    return 'fr_FR'
  }

  return 'en_US'
}

export const changeLocale = newLocale => {
  const mappedLocale = mapLocale(newLocale)
  if (mappedLocale !== newLocale) {
    console.warn(`Locale '${newLocale}' is not supported, falling back to '${mappedLocale}'`)
  }

  console.log(`Changing locale to '${mappedLocale}'`)

  i18n.changeLanguage(mappedLocale)
  networkService.updateUserLocale(mappedLocale)
}

export const t = (key, values = null) => {
  return i18n.t(key, values)
}

export const system = key => i18n.t(`system.${key}`)

export const systemMobileNumber = () => t('system.mobile_number')

export const siteTitle = () => t('intake.site.title')

export const buttonNext = () => t('intake.button.next')
export const buttonPrevious = () => t('intake.button.previous')
export const buttonRegister = () => t('system.register.title')
export const buttonCancel = () => t('system.cancel')
export const buttonSave = () => t('system.save')
export const buttonFinishedAdding = () => t('intake.button.finished.adding')
export const buttonEdit = () => t('intake.button.edit')
export const buttonConfirm = () => t('system.confirm')
export const mobilePhoneHelpText = () => t('system.mobile.phone.help.text')
export const extraRemarks = () => t('system.extra.remarks')
export const requiredField = () => t('intake.yup.required')
export const emailadres = () => t('system.email')
export const yes = () => t('system.yes')
export const fullAddress = () => t('address.full')
export const fullAddressHelptext = () => t('address.full.helptext')
export const notFoundTitle = () => t('not.found.title')
export const notFoundCaption = () => t('not.found.caption')
export const closeWindow = () => t('intake.close.window')
export const intakeFormErrors = () => t('intake.form.errors')

export const formError = code => t(`system.form.error.${code.toLowerCase()}`)

export const usermenuProfile = () => t('intake.usermenu.profile')
export const usermenuHistory = () => t('intake.usermenu.history')
export const usermenuAccount = () => t('intake.usermenu.account')

//Register PAGE

export const authToLogin = () => t('auth.to_login')
export const authSignUpMobileNumberHelpText = () => t('auth.sign_up.mobile_number.help_text')
export const noSmsContactSupport = () => t('sign_up.no_sms_contact_support')

export const registerTitle = () => t('intake.register.title')
export const registerIntro = () => t('intake.register.intro')
export const registerIntroWithBroker = broker => t('intake.register.intro.broker', { broker })
export const registerFirstNameLabel = () => t('intake.register.firstname.label')
export const registerLastNameLabel = () => t('intake.register.lastname.label')
export const registerEmailLabel = () => t('intake.register.email.label')
export const registerPasswordLabel = () => t('intake.register.password.label')
export const registerTacTitle = () => t('intake.register.tac.title')
export const registerTacAccept = () => t('intake.register.tac.accept')
export const registerTacAcceptLink = () => t('intake.register.tac.accept.link')
export const registerWhyTitle = () => t('intake.register.why.title')
export const registerWhyItem1 = () => t('intake.register.why.item.1')
export const registerWhyItem2 = () => t('intake.register.why.item.2')
export const registerWhyItem3 = () => t('intake.register.why.item.3')
export const registerEmailHelpText = () => t('intake.register.email.explenation')

export const registerCompleteProfileTitle = () => t('intake.register.complete_profile.title')
export const registerCompleteProfileIntro = () => t('intake.register.complete_profile.intro')
export const registerCompleteProfileIntroWithBroker = broker =>
  t('intake.register.complete_profile.intro.broker', { broker })

// LOGIN PASSWORD PAGE
export const loginPasswordTitle = () => t('intake.login.password.title')
export const loginPasswordSubTitle = () => t('intake.login.password.sub.title')
export const loginPasswordPasswordLabel = () => t('intake.login.password.password.label')
export const LoginPasswordForgetText = () => t('intake.login.password.forget.password.text')
export const loginPasswordFailedAuthentication = () => t('intake.login.password.authentication.faild')
export const loginPasswordRegex = () => t('regex.password')
export const loginPasswordRegexFailed = () => t('system.password.invalid.regex')
export const loginPasswordRequirements = () => t('system.password.requirements')

// FORGOT PASSWORD PAGE
export const forgotPasswordInputLabel = () => t('auth.phone_or_email.label')
export const forgotPasswordTitle = () => t('intake.forgot.password.title')
export const forgotPasswordSubTitle = () => t('intake.forgot.password.subtitle')
export const forgotPasswordSendTextMsg = () => t('intake.forgot.password.action')
export const forgotPasswordSendResetLink = () => t('auth.request_password_reset.submit.label')
export const forgotPasswordUserNotFound = () => t('auth.forgot.password.user_not_found')
export const forgotPasswordTooManyRequests = () => t('system.error.429')

export const passwordResetRequestSentTitle = () => t('intake.password.reset.request.sent.title')
export const passwordResetRequestLinkSentTitle = () => t('intake.password.reset.request.link.sent.title')
export const passwordResetLinkSentIntro = () => t('auth.request_password_reset.sent.message')
export const passwordResetRequestSentIntro = () => t('intake.password.reset.request.sent.intro')
export const passwordResetTitle = () => t('intake.password.reset.title')
export const passwordResetIntro = () => t('intake.password.reset.intro')
export const passwordResetPasswordLabel = () => t('intake.password.reset.password.label')
export const passwordResetButton = () => t('intake.password.reset.button')
export const passwordResetTokenExpired = () => t('intake.password.reset.token.expired')
export const passwordResetPasswordRequestAgain = () => t('intake.password.reset.request.again')

export const passwordResetSuccessTitle = () => t('intake.password.reset.success.title')
export const passwordResetSuccessIntro = () => t('intake.password.reset.success.into')
export const passwordResetSuccessToLogin = () => t('intake.password.reset.success.action')

// LOGIN PAGE
export const loginMobileNumberPlaceholder = () => t('intake.home.mobile.number.placeholder')
export const enteredMobileNumberPlaceholder = () => t('entered.home.mobile.number.placeholder')
export const loginTitle = () => t('intake.home.title')
export const loginTitleGeneral = () => t('intake.home.title.general')
export const loginMotivation = broker => t('intake.home.motivation', { broker })
export const loginMotivationNoBroker = () => t('intake.home.motivation.no_broker')
export const loginMotivationGeneral = () => t('intake.home.motivation.general')
export const loginSubTitle = () => t('intake.home.title')
export const buttonLogin = () => t('intake.home.button.login')
export const loginPhoneFormatError = () => t('intake.home.phone.number.validation')
export const loginPropertyRef = () => t('intake.home.property.ref')

//verificationview
export const VerificationTitle = () => t('system.verification.title')
export const VerificationMessage = () => t('system.verification.message')
export const verificationPincodeLabel = () => t('intake.verification.pincode.label')
export const verificationWrongPincode = () => t('intake.verification.pincode.wrong')
export const verificationWrongResetCode = () => t('intake.verification.reset.code.wrong')

//email verification view
export const verificationEmailTitle = () => t('email.verification.landingpage.title')
export const verificationEmailMessage = () => t('email.verification.landingpage')
export const verificationEmailToMyProfile = () => t('intake.email.verification.to.my.profile')

// verify email view
export const verifyEmailTitle = () => t('verify_email.info.title')
export const verifyEmailMessage = email => t('verify_email.info.description', { email })
export const verifyEmailNotRecievedMessage = () => t('verify_email.cta.resend_email.disclosure')
export const verifySmsNotRecievedMessage = () => t('verify_sms.cta.resend_sms.disclosure')
export const verifyEmailResend = () => t('verify_email.cta.resend_email')
export const verifySmsResend = () => t('verify_sms.cta.resend_sms')
export const verifyEmailDone = () => t('verify_email.cta.done')
export const verifyEmailNotDone = () => t('verify_email.cta.not_done')
// STEPS
export const stepStart = () => t('intake.step.start')
export const stepResidents = () => t('intake.step.residents')
export const stepRentIntention = () => t('intake.step.rent.intention')
export const stepContract = () => t('intake.step.rent.contract')
export const stepIncome = () => t('intake.step.income')
export const stepDocuments = () => t('intake.step.documents')
export const stepConfirm = () => t('intake.step.confirm')
export const stepProfile = () => t('intake.step.profile')

// Residentsview
export const residentSubTitle = () => t('intake.residents.subtitle')
export const residentTitle = name => t('intake.residents.title', { name })
export const residentCaption = () => t('intake.residents.caption')
export const residentAddPetsAction = () => t('intake.residents.add.pets.action')
export const residentAddPetsTitle = () => t('intake.residents.add.pets.title')

// PETS
export const petsDogs = () => t('intake.pets.dogs')
export const petsCats = () => t('intake.pets.cats')
export const petsFish = () => t('intake.pets.fish')
export const petsRodents = () => t('intake.pets.rodents')
export const petsBirds = () => t('intake.pets.birds')
export const petsChickens = () => t('intake.pets.chickens')
export const petsReptiles = () => t('intake.pets.reptiles')
export const petsOther = () => t('intake.pets.other')

// CONTRACT
export const contractTitle = () => t('intake.contract.title')
export const contractMoreInfoTitle = () => t('intake.contract.id.more.info.title')
export const contractMoreInfoText = () => t('intake.contract.id.more.info.text')

// Rent intentions
export const rentIntentionTitle = step => t(`intake.rent.intention.phase${step}.title`)
export const rentIntentionRentStartDateLabel = () => t('intake.rent.intention.start.date.label')
export const rentIntentionAsapLabel = () => t('intake.rent.intention.asap.label')
export const rentIntentionExactDateLabel = () => t('intake.rent.intention.exact.date.label')
export const rentIntentionExactDateHelpText = () => t('intake.rent.intention.exact.date.help.text')
export const rentIntentionDateError = () => t('intake.rent.intention.date.error')
export const rentIntentionDateErrorNotInPast = () => t('intake.rent.intention.date.error.not.in.past')

// Financial Info
export const financialTitle = () => t('intake.financial.title')
export const financialIntro = () => t('intake.financial.intro')
export const financialInGoodConscience = () => t('intake.financial.in_good_conscience')
export const financialInGoodConscienceError = () => t('intake.financial.in_good_conscience.error')
export const financialMoreInfoAction = () => t('intake.financial.more.info.action')
export const financialMoreInfoTitle = () => t('intake.financial.more.info.title')
export const financialMoreInfoText = () => {
  if (i18n.language.includes('en')) {
    return translationTexts.financialMoreInfoDialog.en
  } else if (i18n.language.includes('fr')) {
    return translationTexts.financialMoreInfoDialog.fr
  } else {
    return translationTexts.financialMoreInfoDialog.nl
  }
}
export const incomeSalary = () => t('intake.income.salary')
export const incomePension = () => t('intake.income.pension')
export const incomeDole = () => t('intake.income.dole')
export const incomeAlimony = () => t('intake.income.alimony')
export const incomeChildBenefit = () => t('intake.income.childbenefit')
export const incomeInterests = () => t('intake.income.interests')
export const incomeSavings = () => t('intake.income.savings')
export const incomeOther = () => t('intake.income.other')
export const selectOneIncome = () => t('intake.income.required')
export const incomeShouldBeOverOne = () => t('intake.income.over.one')
export const financialCheckFeasibilityLabel = () => t('financial.check_feasibility.label')
export const financialExtraRemarksPlaceholder = () => t('financial.extra_remarks.placeholder')
export const financialIncomeStatementHeading = () => t('financial.income_statement.heading')
export const financialIncomeStatementOptionEqualOrMoreLabel = () =>
  t('financial.income_statement.option.equal_or_more.label')
export const financialIncomeStatementOptionLessLabel = () => t('financial.income_statement.option.less.label')
export const financialIncomeStatementOptionRefuseLabel = () => t('financial.income_statement.option.refuse.label')
export const financialIncomeStatementRefuseConfirm = () => t('financial.income_statement.refuse.confirm')

// Documents
export const documentsTaxReturnTitle = () => t('intake.documents.tax.return.title')
export const documentsPayslipTitle = () => t('intake.documents.payslip.title')
export const documentsPayslipDescription = () => t('intake.documents.payslip.description')
export const documentsTitle = () => t('intake.documents.title')
export const documentsStatute = () => t('intake.documents.statute')
export const documentsProofOfSavingsTitle = () => t('intake.documents.proof.of.savings.title')
export const documentsProofOfSavingsDescription = () => t('intake.documents.proof.of.savings.description')
export const documentsStatuteIndefinite = () => t('intakte.documents.statute.indefinite')
export const documentsStatuteDefinite = () => t('intakte.documents.statute.definite')
export const documentsStatuteInterim = () => t('intakte.documents.statute.interim')
export const documentsStatuteSelfEmployed = () => t('intakte.documents.statute.self.employed')
export const documentsHandOff = () => t('intakte.documents.hand.off')
export const documentsProofOfRetirementPaymentTitle = () => t('intake.documents.proof.of.retirement.payment.title')
export const documentsProofOfRetirementPaymentDescription = () =>
  t('intake.documents.proof.of.retirement.payment.description')
export const documentsDoleTitle = () => t('intake.documents.dole.title')
export const documentsDoleDescription = () => t('intake.documents.dole.description')
export const documentsAlimonyTitle = () => t('intake.documents.alimony.title')
export const documentsALimonyDescription = () => t('intake.documents.alimony.description')
export const documentsChildBenefitTitle = () => t('intake.documents.childbenefit.title')
export const documentsChildBenefitDescription = () => t('intake.documents.childbenefit.description')
export const documentsInterestsTitle = () => t('intake.documents.interests.title')
export const documentsInterestsDescription = () => t('intake.documents.interests.description')
export const documentsOtherTitle = () => t('intake.documents.other.title')
export const documentsOtherDescription = () => t('intake.documents.other.description')
export const documentsFileTypeNotAllowed = name => t('intake.documents.file.type.not.allowed', { name })
export const documentsFileLimitExceeded = (name, limit) => t('intake.documents.file.limit.exceeded', { name, limit })
export const documentsFileSizeExceeded = (name, fileSize, maxSize) =>
  t('intake.documents.file.size.exceeded', { name, fileSize, maxSize })
export const documentsFileUnableToUpload = name => t('intake.documents.unable.to.upload', { name })
export const documentsFileUnableToRemove = () => t('intake.documents.unable.to.remove')
export const selectOneFile = () => t('intake.documents.required')
export const fileIsUploading = name => t('SYSTEM.FILE.IS.UPLOADING', { name })
export const documentIntro = () => t('intake.documents.intro')
export const documentMoreInfoAction = () => t('intake.documents.more.info.action')
export const documentIntroBis = () => t('intake.documents.intro.bis')
export const documentMoreInfoTitle = () => t('intake.documents.more.info.title')
export const documentMoreInfoText = () => t('intake.documents.more.info.text')

// Start
export const startTitle = name => t('inatke.start.phase.2.title', { name })
export const startIntro = () => t('intake.start.intro')

// Overview
export const applicationAcceptDataUsage = () => t('application.accept.data_usage')
export const overviewAcceptDataUsage = () => t('overview.accept.datausage')
export const overviewAcceptDataUsageError = () => t('overview.accept.datausage.error')
export const overviewCheckboxLabel = () => t('intake.overview.checkbox.confirmation.email.label')
export const overviewCheckboxLabelExtra = () => t('intake.overview.checkbox.confirmation.email.extra')
export const overviewTitle = (name, broker, step) => {
  const stepType = getCurrentStepType(step)
  return stepType === 'CANDIDACY_REQUEST_STEPS'
    ? t('intake.overview.send.for.candidacy.title', { name })
    : t('intake.overview.send.for.visit.title', { name })
}

export const overviewTitleInvalid = (name, broker, step) => {
  const stepType = getCurrentStepType(step)
  return stepType === 'CANDIDACY_REQUEST_STEPS'
    ? t('intake.overview.send.for.candidacy.invalid', { name, broker })
    : t('intake.overview.send.for.visit.invalid', { name, broker })
}
export const overviewSendAction = step => {
  const stepType = getCurrentStepType(step)
  if (stepType !== 'VISIT_REQUEST_STEPS') {
    return t('intake.overview.send.for.candidacy.action')
  } else {
    return t('intake.overview.send.for.visit.action')
  }
}
export const overviewResendAction = () => t('intake.overview.send.update')
export const overviewResendBackToProfile = () => t('intake.overview.send.back_to_profile')
export const overviewAplicationAlreadySubmitted = step => {
  const stepType = getCurrentStepType(step)
  if (stepType === 'CANDIDACY_REQUEST_STEPS') {
    return t('intake.overview.candidacy.already.submitted')
  } else {
    return t('intake.overview.visit.already.submitted')
  }
}
export const overviewAplicationAlreadyLink = step => {
  const stepType = getCurrentStepType(step)
  if (stepType === 'CANDIDACY_REQUEST_STEPS') {
    return t('intake.overview.candidacy.already.link')
  } else {
    return t('intake.overview.visit.already.link')
  }
}
export const overviewEditProfile = name => t('intake.overview.edit.profile', { name })
export const overviewStartCandidacyFlow = name => t('intake.overview.start.candidacy.flow', { name })
export const overviewStepsNotValid = () => t('overview.steps.not_valid')
export const residentsSummary = (adultsCount, childrensCount, petsCount) => {
  if (petsCount !== undefined) {
    return t('intake.overview.summary.residents_with_pets', {
      adultsCount,
      childrensCount,
      petsCount,
    })
  } else {
    return t('intake.overview.summary.residents', {
      adultsCount,
      childrensCount,
    })
  }
}

export const rentIntentionSummary = occupancyDate =>
  t('intake.overview.summary.rent.intention', {
    occupancyDate: occupancyDate ? moment(occupancyDate).format('l') : '-',
  })
export const contractSummary = contactsList => t('intake.overview.summary.contract', { contactsList })
export const incomeSummary = incomesCount => t('intake.overview.summary.income', { incomesCount })
export const documentsSummary = documentsCount => t('intake.overview.summary.documents', { documentsCount })
export const sendPdfToEmail = () => t('intake.overview.send.pdf.to.email')
export const sendEmailToButtonText = () => t('intake.step.confirm')
export const downloadPDF = () => t('intake.download.pdf')

// Sent
export const sentTitle = (name, step) => {
  const stepType = getCurrentStepType(step)
  if (stepType !== 'VISIT_REQUEST_STEPS') {
    return t('intake.sent.for.candidacy.title', { name })
  } else {
    return t('intake.sent.for.visit.title', { name })
  }
}
export const sentIntro = (step, broker) => {
  const stepType = getCurrentStepType(step)
  if (stepType !== 'VISIT_REQUEST_STEPS') {
    return t('intake.sent.for.candidacy.intro', { broker })
  } else {
    return t('intake.sent.for.visit.intro', { broker })
  }
}

export const sentProfileRequests = step => {
  const stepType = getCurrentStepType(step)
  if (stepType !== 'VISIT_REQUEST_STEPS') {
    return t('intake.sent.edit.profile.requests')
  } else {
    return t('intake.sent.edit.profile.requests.visit')
  }
}
export const sentProfileRequestsActions = step => {
  const stepType = getCurrentStepType(step)
  if (stepType !== 'VISIT_REQUEST_STEPS') {
    return t('intake.sent.edit.profile.action.requests')
  } else {
    return t('intake.sent.edit.profile.action.requests.visit')
  }
}

export const generalError = () => t('general.error')

export const personalInformationAndHistoryButton = () => t('intake.personal.info.and.history.button')
export const removeDataOverviewButton = () => t('intake.data.overview.button')

export const intakeRemoveAtBroker = () => t('intake.remove.at.broker')
export const intakeRemoveAtRentio = () => t('intake.remove.at.rentio')
export const intakeRemoveByBroker = () => t('intake.removed.by.broker')
export const intakeRemovedApplication = () => t('intake.removed.application')
export const intakeRemoveApplication = () => t('intake.remove.application')
export const intakeRemoveAtBrokerPending = () => t('intake.removed.at.broker.pending')
export const intakeRemoveApplicationPending = () => t('intake.removed.application.pending')

// yup vertalingen
export function setLocalChangeYup() {
  setLocale({
    mixed: {
      required: t('intake.yup.required'),
    },
    string: {
      min: t('intake.yup.string.min'),
    },
  })
}

export function setLocaleChangeMoments(locale) {
  moment.locale(locale)
}

export const profileDeleteApplicationsTitle = () => t('intake.profile.delete.applications')
export const profileResendApplication = step => {
  const stepType = getCurrentStepType(step)
  if (stepType === 'VISIT_REQUEST_STEPS') {
    return t('intake.profile.resend.application.visit')
  } else {
    return t('intake.profile.resend.application')
  }
}

export const profileHistoryInfoHistoryTitle = () => t('intake.profile.history.info.history.title')
export const profileHistoryInfoProfileTitle = () => t('intake.profile.history.info.profile.title')
export const profileHistoryInfoDateHeader = () => t('intake.profile.history.info.date.header')
export const profileHistoryInfoPropertyHeader = () => t('intake.profile.history.info.property.header')
export const profileHistoryInfoPriceHeader = () => t('intake.profile.history.info.price.header')
export const profileHistoryInfoBrokerHeader = () => t('intake.profile.history.info.broker.header')
export const profileHistoryInfoDocumentsHeader = () => t('intake.profile.history.info.document.header')
export const profileHistoryInfoEmailHelpText = () => t('intake.profile.history.info.email.help.text')
export const profileHistoryInfoUnverifiedText = () => t('intake.profile.history.info.unverified.text')
export const profileHistoryApplicationPdfStatus = pdfStatus => t(`application.pdf.status.${pdfStatus}`)

export const accountPrivacyDateAccepted = () => t('account.privacy.date_accepted')
export const accountPrivacyTitle = () => t('account.privacy.title')

export const profileResendVerificationMail = () => t('intake.profile.resend.verification_mail')
export const profileResendVerificationMailSent = () => t('intake.profile.resend.verification_mail.sent')

export const myRentProfile = () => t('intake.my.rent.profile')
export const mySubmissionHistory = () => t('intake.my.submission.history')
export const mySubmissionHistoryEmptyTitle = () => t('intake.my.submission.history.empty.title')
export const mySubmissionHistoryEmptyText = () => t('intake.my.submission.history.empty.text')
export const myAccount = () => t('intake.my.account')
export const deleteAccountButton = () => t('account.delete.button')
export const deleteAccountConfirm = () => t('account.delete.confirm')
export const deleteAccountFailed = () => t('account.delete.failed')

export const privacyTitle = () => t('privacy.title')
export const privacyConditionsText = () => {
  if (i18n.language.includes('en')) {
    return translationTexts.privacyConditionsText.en
  } else if (i18n.language.includes('fr')) {
    return translationTexts.privacyConditionsText.fr
  } else {
    return translationTexts.privacyConditionsText.nl
  }
}

export const TACUpdateHeading = () => i18n.t('tac.update.title')
export const TACUpdateDescription = () => i18n.t('tac.update.description')
export const TACUpdateAcceptAction = () => i18n.t('tac.update.cta.accept')



// WEBPACK FOOTER //
// ./src/services/translationService.js